/**
 * The marketplace's Titles category.
 *
 * Why titles exist at all is in `backend/src/game/titles/titles.types.ts`: the shop had four
 * items and cannot simply have more skins, because every POLYGON body in the licensed pack is
 * already used by the two team defaults and the four catalogue skins. A title needs no art of
 * its own, so its tile draws a small kind glyph (star/xp/rank) instead of a picture.
 *
 * Equipping is here too, not on a separate screen — a title is bought and worn in one place
 * because there is nothing to preview.
 */
import { api } from '../../../core/network/api';
import { confirmAction, icon } from '../ui';
import { formatNumber, t } from '../../i18n';
import type { MarketItem } from './marketplace-tile';
import { createMarketSection, type MarketSection } from './marketplace-section';

interface TitleEntry {
  slug: string;
  name: string;
  description: string;
  unlock:
    | { kind: 'purchase'; softCost: number }
    | { kind: 'level'; level: number }
    | { kind: 'rank'; points: number };
  owned: boolean;
  equipped: boolean;
  affordable: boolean;
}

interface TitleCatalogue {
  progress: { level: number; rankPoints: number; softBalance: number };
  titles: TitleEntry[];
  equipped: string | null;
}

function requirementLabel(entry: TitleEntry): string | undefined {
  switch (entry.unlock.kind) {
    case 'level':
      return t('common.unlockLevel', { level: entry.unlock.level });
    case 'rank':
      return t('common.unlockPoints', { points: formatNumber(entry.unlock.points) });
    default:
      return undefined;
  }
}

function kindGlyph(entry: TitleEntry, dimmed: boolean): HTMLElement {
  const kind = entry.unlock.kind === 'purchase' ? 'star' : entry.unlock.kind === 'level' ? 'xp' : 'rank';
  return icon(kind, { size: 28, className: dimmed ? 'opacity-70' : '' });
}

function toItem(entry: TitleEntry, act: (mutate: () => Promise<unknown>) => void, busy: boolean): MarketItem {
  const purchase = entry.unlock.kind === 'purchase';

  return {
    key: `title-${entry.slug}`,
    category: 'titles',
    name: entry.name,
    description: entry.description,
    art: kindGlyph(entry, entry.owned),
    price: purchase ? { amount: (entry.unlock as { softCost: number }).softCost, currency: 'SOFT' } : null,
    requirement: requirementLabel(entry),
    owned: entry.owned,
    equipped: entry.equipped,
    affordable: entry.affordable,
    // A level/rank title unowned has nothing to press — the requirement already says what to do.
    hint: !entry.owned && !purchase ? t('shop.earn') : undefined,
    action: !entry.owned
      ? purchase
        ? {
            label: t('common.buy'),
            variant: 'primary',
            disabled: busy || !entry.affordable,
            onClick: () =>
              void (async () => {
                const softCost = (entry.unlock as { softCost: number }).softCost;
                const confirmed = await confirmAction({
                  title: t('confirmDialog.buyTitle'),
                  body: t('confirmDialog.buyBody', { item: entry.name, price: `${formatNumber(softCost)} ${t('common.credits')}` }),
                });
                if (!confirmed) return;
                act(() => api.post('/game/titles/purchase', { title: entry.slug }));
              })(),
          }
        : undefined
      : !entry.equipped
        ? {
            label: t('shop.titles.wear'),
            variant: 'secondary',
            disabled: busy,
            onClick: () =>
              void (async () => {
                const confirmed = await confirmAction({
                  title: t('confirmDialog.wearTitle'),
                  body: t('confirmDialog.wearBody', { item: entry.name }),
                });
                if (!confirmed) return;
                act(() => api.post('/game/titles/equip', { title: entry.slug }));
              })(),
          }
        : {
            label: t('common.remove'),
            variant: 'ghost',
            disabled: busy,
            onClick: () => act(() => api.post('/game/titles/equip', {})),
          },
  };
}

export function createTitlesSection(): MarketSection {
  return createMarketSection<TitleCatalogue>({
    id: 'titles',
    label: t('shop.titles.title'),
    note: t('shop.titles.note'),
    fetchCatalogue: () => api.get<TitleCatalogue>('/game/titles'),
    toItems: (catalogue, act, busy) => catalogue.titles.map((entry) => toItem(entry, act, busy)),
  });
}
